import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TENANT_ID, API_BASE_URL } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import '../styles/ai-providers.css';

interface AIEmbeddingProvider {
  id: string;
  driver: 'jina' | 'mistral' | 'gemini' | 'embeddinggemma' | 'jina-local' | 'openrouter';
  model: string;
  baseUrl: string | null;
  apiKey: string | null;
  maxRequestsPerDay: number;
  maxTokensPerDay: number;
  priority: number;
  enabled: boolean;
  inputCostPerMillion?: number | null;
  outputCostPerMillion?: number | null;
}

const emptyProvider: Omit<AIEmbeddingProvider, 'id'> = {
  driver: 'embeddinggemma',
  model: 'google/embeddinggemma-2',
  baseUrl: 'http://127.0.0.1:8080',
  apiKey: '',
  maxRequestsPerDay: 100000,
  maxTokensPerDay: 50_000_000,
  priority: 1,
  enabled: true,
  inputCostPerMillion: null,
  outputCostPerMillion: null,
};

const driverInfo: Record<string, { icon: string; color: string; label: string; description: string; defaultModel: string }> = {
  jina: { 
    icon: '🧠', 
    color: '#00A67E', 
    label: 'Jina Cloud',
    description: 'Jina AI embeddings - API oficial',
    defaultModel: 'jina-embeddings-v3'
  },
  mistral: { 
    icon: '🌫', 
    color: '#FF7000', 
    label: 'Mistral',
    description: 'Mistral AI embeddings - Optimizado para español',
    defaultModel: 'mistral-embed'
  },
  gemini: { 
    icon: '🤖', 
    color: '#4285F4', 
    label: 'Gemini',
    description: 'Google Gemini embeddings - Multimodal',
    defaultModel: 'text-embedding-004'
  },
  embeddinggemma: {
    icon: '⚡',
    color: '#8B5CF6',
    label: 'Embedding Gemma (TEI)',
    description: 'Self-hosted Text Embeddings Inference (HuggingFace)',
    defaultModel: 'google/embeddinggemma-2'
  },
  'jina-local': {
    icon: '🏠',
    color: '#10B981',
    label: 'Jina Local',
    description: 'Instancia auto-alojada de Jina v2',
    defaultModel: 'jinaai/jina-embeddings-v2-base-en'
  },
  openrouter: {
    icon: '🔀',
    color: '#6366F1',
    label: 'OpenRouter',
    description: 'OpenRouter Embeddings API (google/gemini-embedding-2 - Multimodal)',
    defaultModel: 'google/gemini-embedding-2'
  },
};

const defaultEmbeddingDriverInfo = { 
  icon: '🧠', 
  color: '#6B7280', 
  label: 'Otro',
  description: 'Proveedor de embeddings',
  defaultModel: ''
};

const getEmbeddingDriverInfo = (driver: string) => (driverInfo as Record<string, typeof driverInfo['jina']>)[driver] || defaultEmbeddingDriverInfo;

const AIEmbeddingProvidersPage: React.FC = () => {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [providers, setProviders] = useState<AIEmbeddingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AIEmbeddingProvider | null>(null);
  const [form, setForm] = useState<Omit<AIEmbeddingProvider, 'id'>>(emptyProvider);
  const [showModal, setShowModal] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; latency?: number } | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [fetchingPricing, setFetchingPricing] = useState(false);

  const handleConsultPricing = async () => {
    if (!form.model.trim()) {
      addToast({ type: 'error', title: 'Validación', message: 'Debes indicar un modelo para consultar su tarifa' });
      return;
    }
    setFetchingPricing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/embedding-providers/query-pricing`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: form.model.trim(),
          driver: form.driver,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({
          ...prev,
          inputCostPerMillion: data.inputCostPerMillion ?? 0,
          outputCostPerMillion: data.outputCostPerMillion ?? 0,
        }));
        addToast({
          type: 'info',
          title: 'Tarifa oficial consultada',
          message: data.isFreeTier
            ? 'Modelo identificado como Free Tier ($0.00 USD)'
            : `Tarifa catálogo: ${data.inputCostPerMillion} in / ${data.outputCostPerMillion} out / 1M`,
        });
      } else {
        const errData = await res.json().catch(() => ({}));
        addToast({
          type: 'warning',
          title: 'Consulta no disponible',
          message: errData.error || 'No se encontró tarifa automática para este modelo',
        });
      }
    } catch (err: any) {
      console.warn('Could not query pricing:', err);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo consultar la tarifa del modelo',
      });
    } finally {
      setFetchingPricing(false);
    }
  };

  const headers: HeadersInit = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
  };

  const loadProviders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/embedding-providers`, { headers });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || 'Failed to load AI embedding providers');
      }
      const data = await res.json();
      setProviders(data);
    } catch (err) {
      console.error(err);
      addToast({ 
        type: 'error', 
        title: 'Error de conexión', 
        message: err instanceof Error ? err.message : 'No se pudieron cargar los proveedores de embeddings. Verifica que el backend esté corriendo.' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!form.model.trim()) {
      errors.model = 'El modelo es requerido';
    }
    
    if (form.maxRequestsPerDay < 1) {
      errors.maxRequestsPerDay = 'Debe ser mayor a 0';
    }
    
    if (form.maxTokensPerDay < 1) {
      errors.maxTokensPerDay = 'Debe ser mayor a 0';
    }
    
    if (form.priority < 1) {
      errors.priority = 'Debe ser mayor a 0';
    }
    
    if (form.baseUrl && !form.baseUrl.startsWith('http')) {
      errors.baseUrl = 'Debe ser una URL válida (http:// o https://)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addToast({ type: 'error', title: 'Validación', message: 'Por favor corrige los errores en el formulario' });
      return;
    }
    
    setTesting(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing
        ? `${API_BASE_URL}/admin/embedding-providers/${editing.id}`
        : `${API_BASE_URL}/admin/embedding-providers`;
      
      // Prepare data: convert empty strings to undefined for optional fields
      const submitData = {
        ...form,
        baseUrl: form.baseUrl?.trim() || undefined,
        apiKey: form.apiKey?.trim() || undefined,
        inputCostPerMillion: form.inputCostPerMillion !== null && form.inputCostPerMillion !== undefined && (form.inputCostPerMillion as any) !== ''
          ? Number(form.inputCostPerMillion)
          : null,
        outputCostPerMillion: form.outputCostPerMillion !== null && form.outputCostPerMillion !== undefined && (form.outputCostPerMillion as any) !== ''
          ? Number(form.outputCostPerMillion)
          : null,
      };
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(submitData),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Error desconocido' }));
        console.error('[AIEmbeddingProviders] Error response:', errorData);
        
        // Handle Zod validation errors
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map((err: any) => 
            `${err.path?.join('.')}: ${err.message}`
          ).join(', ');
          throw new Error(errorMessages);
        }
        
        throw new Error(errorData.message || errorData.error || 'Failed to save provider');
      }
      
      await loadProviders();
      setShowModal(false);
      setEditing(null);
      setForm(emptyProvider);
      setTestResult(null);
      setFormErrors({});
      addToast({ 
        type: 'success', 
        title: '✅ Éxito', 
        message: editing ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente' 
      });
    } catch (err) {
      console.error('[AIEmbeddingProviders] Submit error:', err);
      addToast({ 
        type: 'error', 
        title: 'Error', 
        message: err instanceof Error ? err.message : 'No se pudo guardar el proveedor' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleEdit = (provider: AIEmbeddingProvider) => {
    setEditing(provider);
    setForm({
      driver: provider.driver,
      model: provider.model,
      baseUrl: provider.baseUrl || '',
      apiKey: provider.apiKey || '',
      maxRequestsPerDay: provider.maxRequestsPerDay,
      maxTokensPerDay: provider.maxTokensPerDay,
      priority: provider.priority,
      enabled: provider.enabled,
      inputCostPerMillion: provider.inputCostPerMillion !== undefined ? provider.inputCostPerMillion : null,
      outputCostPerMillion: provider.outputCostPerMillion !== undefined ? provider.outputCostPerMillion : null,
    });
    setTestResult(null);
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!confirm(`¿Estás seguro de eliminar el proveedor "${provider?.model}"?\n\nEsta acción no se puede deshacer.`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/admin/embedding-providers/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || 'Failed to delete provider');
      }
      await loadProviders();
      addToast({ type: 'success', title: '🗑️ Eliminado', message: 'Proveedor eliminado correctamente' });
    } catch (err) {
      console.error(err);
      addToast({ 
        type: 'error', 
        title: 'Error', 
        message: err instanceof Error ? err.message : 'No se pudo eliminar el proveedor' 
      });
    }
  };

  const testConnection = async () => {
    if (!form.model.trim()) {
      addToast({ type: 'error', title: 'Validación', message: 'Debes especificar un modelo antes de probar' });
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/embedding-providers/test-connection`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          driver: form.driver,
          model: form.model,
          baseUrl: form.baseUrl || undefined,
          apiKey: form.apiKey || undefined,
        }),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || 'Test failed');
      }
      
      const result = await res.json();
      if (result.ok) {
        setTestResult({ success: true, message: 'Conexión exitosa', latency: result.latencyMs });
        addToast({ 
          type: 'success', 
          title: '✅ Conexión exitosa', 
          message: `Latencia: ${result.latencyMs}ms - El proveedor está funcionando correctamente` 
        });
      } else {
        setTestResult({ success: false, message: result.sampleText || 'Error en la conexión' });
        addToast({ 
          type: 'error', 
          title: '❌ Error de conexión', 
          message: result.sampleText || 'No se pudo conectar con el proveedor' 
        });
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : 'No se pudo probar la conexión';
      setTestResult({ success: false, message: errorMessage });
      addToast({ type: 'error', title: 'Error', message: errorMessage });
    } finally {
      setTesting(false);
    }
  };

  const openModal = () => {
    setEditing(null);
    setForm(emptyProvider);
    setTestResult(null);
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    if (testing) return;
    setShowModal(false);
    setEditing(null);
    setForm(emptyProvider);
    setTestResult(null);
    setFormErrors({});
  };

  const handleDriverChange = (driver: 'jina' | 'mistral' | 'gemini' | 'embeddinggemma' | 'jina-local' | 'openrouter') => {
    setForm({ 
      ...form, 
      driver,
      model: driverInfo[driver]?.defaultModel || '',
      baseUrl: driver === 'openrouter' && !form.baseUrl ? 'https://openrouter.ai/api/v1' : form.baseUrl
    });
    setFormErrors({ ...formErrors, model: '' });
  };

  return (
    <div className="ai-providers-page">
      <div className="page-header">
        <div>
          <h2>🧠 Proveedores de Embeddings AI</h2>
          <p className="page-subtitle">
            Configura y gestiona los proveedores de embeddings para búsqueda semántica y FAQs
          </p>
        </div>
        <button className="btn btn-primary" onClick={openModal}>
          ➕ Agregar Proveedor
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando proveedores...</p>
        </div>
      ) : providers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🤖</div>
          <h3>No hay proveedores configurados</h3>
          <p>Agrega tu primer proveedor de embeddings para habilitar búsqueda semántica</p>
          <button className="btn btn-primary" onClick={openModal}>
            ➕ Agregar Primer Proveedor
          </button>
        </div>
      ) : (
        <div className="providers-grid">
          {providers.map((provider) => (
            <div key={provider.id} className={`provider-card ${!provider.enabled ? 'disabled' : ''}`}>
              <div className="provider-card-header">
                {(() => {
                  const dInfo = getEmbeddingDriverInfo(provider.driver);
                  return (
                    <>
                      <div 
                        className="provider-icon" 
                        style={{ 
                          background: `linear-gradient(135deg, ${dInfo.color}20, ${dInfo.color}40)`,
                          color: dInfo.color
                        }}
                      >
                        {dInfo.icon}
                      </div>
                      <div className="provider-info">
                        <h3>{provider.model}</h3>
                        <div className="provider-driver">{dInfo.label}</div>
                      </div>
                    </>
                  );
                })()}
                <div className="provider-status">
                  <span className={`status-badge ${provider.enabled ? 'active' : 'inactive'}`}>
                    {provider.enabled ? '✓ Activo' : '○ Inactivo'}
                  </span>
                </div>
              </div>
              
              <div className="provider-card-body">
                <div className="provider-detail">
                  <span className="detail-label">📊 Requests/día</span>
                  <span className="detail-value">{provider.maxRequestsPerDay.toLocaleString()}</span>
                </div>
                <div className="provider-detail">
                  <span className="detail-label">🔤 Tokens/día</span>
                  <span className="detail-value">{provider.maxTokensPerDay.toLocaleString()}</span>
                </div>
                <div className="provider-detail">
                  <span className="detail-label">⚡ Prioridad</span>
                  <span className={`priority-badge ${provider.priority === 1 ? 'primary' : ''}`}>
                    #{provider.priority}
                  </span>
                </div>
                {provider.baseUrl && (
                  <div className="provider-detail">
                    <span className="detail-label">🔗 Base URL</span>
                    <span className="detail-value truncate" title={provider.baseUrl}>
                      {provider.baseUrl}
                    </span>
                  </div>
                )}
                <div className="provider-detail">
                  <span className="detail-label">💵 Costo / 1M</span>
                  <span className="detail-value">
                    {provider.inputCostPerMillion === 0 && (provider.outputCostPerMillion === 0 || provider.outputCostPerMillion == null) ? (
                      <span className="status-badge" style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
                        Free Tier ($0.00)
                      </span>
                    ) : provider.inputCostPerMillion != null ? (
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        ${provider.inputCostPerMillion} in{provider.outputCostPerMillion ? ` / ${provider.outputCostPerMillion} out` : ''}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>Sin definir</span>
                    )}
                  </span>
                </div>
              </div>
              
              <div className="provider-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(provider)}>
                  ✏️ Editar
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(provider.id)}>
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? '✏️ Editar Proveedor' : '➕ Agregar Proveedor'}</h3>
              <button className="modal-close" onClick={closeModal} disabled={testing}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="provider-form">
              <div className="form-section">
                <h4>🔧 Configuración Básica</h4>
                
                <div className="form-group">
                  <label>Driver de Embeddings</label>
                  <select
                    value={form.driver}
                    onChange={(e) => handleDriverChange(e.target.value as any)}
                    required
                    disabled={testing}
                  >
                    {Object.entries(driverInfo).map(([key, info]) => (
                      <option key={key} value={key}>
                        {info.icon} {info.label} - {info.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Modelo {formErrors.model && <span style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>({formErrors.model})</span>}
                  </label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={(e) => {
                      setForm({ ...form, model: e.target.value });
                      setFormErrors({ ...formErrors, model: '' });
                    }}
                    placeholder={`ej: ${driverInfo[form.driver].defaultModel}`}
                    required
                    disabled={testing}
                    style={formErrors.model ? { borderColor: 'var(--error-color)' } : {}}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    💡 Modelo sugerido: {driverInfo[form.driver].defaultModel}
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Base URL (opcional) {formErrors.baseUrl && <span style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>({formErrors.baseUrl})</span>}
                  </label>
                  <input
                    type="url"
                    value={form.baseUrl ?? ''}
                    onChange={(e) => {
                      setForm({ ...form, baseUrl: e.target.value });
                      setFormErrors({ ...formErrors, baseUrl: '' });
                    }}
                    placeholder="https://api.example.com"
                    disabled={testing}
                    style={formErrors.baseUrl ? { borderColor: 'var(--error-color)' } : {}}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    🌐 Deja vacío para usar la URL por defecto del proveedor
                  </small>
                </div>

                <div className="form-group">
                  <label>API Key (opcional)</label>
                  <input
                    type="password"
                    value={form.apiKey ?? ''}
                    onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                    placeholder="Clave API global (los tenants pueden sobreescribir)"
                    disabled={testing}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    🔑 Fallback global si el tenant no tiene su propia API key
                  </small>
                </div>
              </div>

              <div className="form-section pricing-form-section">
                <div className="pricing-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>💰 Estructura de Costos de Tokens ($ USD / 1M tokens)</h4>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleConsultPricing}
                    disabled={fetchingPricing || !form.model.trim()}
                    title="Consultar tarifa de mercado oficial para este modelo"
                  >
                    {fetchingPricing ? 'Consultando...' : '⚡ Consultar Tarifa Oficial'}
                  </button>
                </div>
                <p className="pricing-section-desc" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.75rem 0' }}>
                  Tarifas por millón (1M) de tokens de entrada (texto / imágenes) tomadas en cuenta para medir el costo de generación de embeddings en la plataforma.
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tarifa Entrada (Prompt) / 1M tokens</label>
                    <div className="input-currency-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <span className="currency-symbol" style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)' }}>$</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        name="inputCostPerMillion"
                        style={{ paddingLeft: '24px' }}
                        value={form.inputCostPerMillion !== null && form.inputCostPerMillion !== undefined ? form.inputCostPerMillion : ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : Number(e.target.value);
                          setForm({ ...form, inputCostPerMillion: val });
                        }}
                        placeholder="Ej: 0.20 (o 0 para Free Tier)"
                      />
                    </div>
                    <small className="form-hint" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Costo en USD por 1M tokens de texto/imágenes indexados</small>
                  </div>

                  <div className="form-group">
                    <label>Tarifa Salida (Completion) / 1M tokens</label>
                    <div className="input-currency-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <span className="currency-symbol" style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)' }}>$</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        name="outputCostPerMillion"
                        style={{ paddingLeft: '24px' }}
                        value={form.outputCostPerMillion !== null && form.outputCostPerMillion !== undefined ? form.outputCostPerMillion : ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : Number(e.target.value);
                          setForm({ ...form, outputCostPerMillion: val });
                        }}
                        placeholder="Normalmente 0.00 para embeddings"
                      />
                    </div>
                    <small className="form-hint" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Normalmente $0.00 para modelos de embedding</small>
                  </div>
                </div>

                {form.inputCostPerMillion !== null && (
                  <div className="pricing-sample-pill" style={{
                    marginTop: '0.75rem',
                    padding: '0.6rem 0.85rem',
                    background: 'var(--bg-secondary, #f8fafc)',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    border: '1px solid var(--border-color, #e2e8f0)',
                  }}>
                    {Number(form.inputCostPerMillion) === 0 ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>
                        🎉 Modelo configurado como Free Tier ($0.00 USD por indexación)
                      </span>
                    ) : (
                      <span>
                        📊 <strong>Indexación típica</strong> (1,000 tokens de texto / imagen):{' '}
                        <code style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                          ${Number(((1000 * (Number(form.inputCostPerMillion) || 0)) / 1000000).toFixed(6))} USD
                        </code>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h4>📊 Límites y Prioridad</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Máx Requests/Día {formErrors.maxRequestsPerDay && <span style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>({formErrors.maxRequestsPerDay})</span>}
                    </label>
                    <input
                      type="number"
                      value={form.maxRequestsPerDay}
                      onChange={(e) => {
                        setForm({ ...form, maxRequestsPerDay: Number(e.target.value) });
                        setFormErrors({ ...formErrors, maxRequestsPerDay: '' });
                      }}
                      min={1}
                      required
                      disabled={testing}
                      style={formErrors.maxRequestsPerDay ? { borderColor: 'var(--error-color)' } : {}}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Máx Tokens/Día {formErrors.maxTokensPerDay && <span style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>({formErrors.maxTokensPerDay})</span>}
                    </label>
                    <input
                      type="number"
                      value={form.maxTokensPerDay}
                      onChange={(e) => {
                        setForm({ ...form, maxTokensPerDay: Number(e.target.value) });
                        setFormErrors({ ...formErrors, maxTokensPerDay: '' });
                      }}
                      min={1}
                      required
                      disabled={testing}
                      style={formErrors.maxTokensPerDay ? { borderColor: 'var(--error-color)' } : {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Prioridad {formErrors.priority && <span style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>({formErrors.priority})</span>}
                  </label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => {
                      setForm({ ...form, priority: Number(e.target.value) });
                      setFormErrors({ ...formErrors, priority: '' });
                    }}
                    min={1}
                    required
                    disabled={testing}
                    style={formErrors.priority ? { borderColor: 'var(--error-color)' } : {}}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    ⚡ Menor número = mayor prioridad (1 es la más alta)
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      disabled={testing}
                    />
                    <span>✓ Proveedor habilitado</span>
                  </label>
                </div>
              </div>

              {testResult && (
                <div style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  background: testResult.success ? '#d1fae5' : '#fee2e2',
                  color: testResult.success ? '#065f46' : '#991b1b',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{testResult.success ? '✅' : '❌'}</span>
                  <div>
                    <strong>{testResult.success ? 'Conexión exitosa' : 'Error de conexión'}</strong>
                    <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                      {testResult.message}
                      {testResult.latency && ` (${testResult.latency}ms)`}
                    </div>
                  </div>
                </div>
              )}

              <button 
                type="button" 
                className="btn btn-test" 
                onClick={testConnection} 
                disabled={testing || !form.model.trim()}
              >
                {testing ? '🔄 Probando conexión...' : '🧪 Probar Conexión'}
              </button>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={testing}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={testing}>
                  {testing ? '⏳ Guardando...' : editing ? '💾 Actualizar' : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIEmbeddingProvidersPage;
